import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoBusinessOutline,
  IoCallOutline,
  IoCalendarOutline,
  IoHelpCircle,
} from 'react-icons/io5';
import {
  resolveShortLink,
  submitShortLinkResponse,
  submitShortLinkAction,
} from '../../services/shortlinkService';
import ScheduleDialPicker from '../../components/common/ScheduleDialPicker';

const CONTACT_PHONE = '070-7666-9413';

const CATEGORY_LABEL = {
  moving: '이사',
  internet_tv: '인터넷/TV',
  cleaning: '입주청소',
  interior: '인테리어',
  aircon: '가전렌탈',
  water_purifier: '정수기렌탈',
  kiosk: '키오스크',
};

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

function parseDate(input) {
  if (!input) return null;
  const s = String(input);
  let dt;
  if (s.includes('T') || (s.includes(' ') && s.includes(':'))) {
    dt = new Date(s.replace(' ', 'T'));
  } else if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    dt = new Date(s + 'T00:00:00');
  } else {
    dt = new Date(s);
  }
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatDateKR(input) {
  const dt = parseDate(input);
  if (!dt) return String(input || '');
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  const w = WEEKDAY_KR[dt.getDay()];
  return `${y}.${m}.${d} (${w})`;
}

function formatTimeKR(input) {
  const dt = parseDate(input);
  if (!dt) return '';
  if (dt.getHours() === 0 && dt.getMinutes() === 0) return '';
  const h = dt.getHours();
  const mm = String(dt.getMinutes()).padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  const hr12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${ampm} ${hr12}:${mm}`;
}

// 응답 종류별 화면 구성 (input 단계)
const KIND_META = {
  schedule_change: {
    title: '희망 일정을 입력해주세요',
    desc: '변경을 원하시는 날짜와 시간을 알려주시면 담당 업체가 확인 후 다시 안내드립니다.',
    needsDate: true,
    memoLabel: '추가 전달사항 (선택)',
  },
  inquiry: {
    title: '문의 내용을 남겨주세요',
    desc: '남겨주신 내용은 본사에서 확인 후 처리해드립니다.',
    needsDate: false,
    memoLabel: '문의 내용',
  },
  partner_change: {
    title: '다른 업체 연결 요청',
    desc: '요청 사유나 전달사항을 남겨주시면 다른 업체로 다시 매칭해드립니다.',
    needsDate: false,
    memoLabel: '요청 내용',
  },
};

export default function RespondInputPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const previewDone = searchParams.get('done') === '1';
  const previewStep = searchParams.get('step') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [link, setLink] = useState(null);

  // step: 'confirm' | 'input' | 'verify' | 'done'
  const [step, setStep] = useState(previewDone ? 'done' : (previewStep || 'confirm'));

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [memo, setMemo] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    if (previewDone) {
      setStep('done');
    } else if (previewStep === 'change' || previewStep === 'confirm') {
      setStep(previewStep);
    }
    if (previewDone || previewStep) {
      setLink({
        customerName: '이수영',
        customerAddress: '서울 강서구 공항대로 110 101동 1203호',
        category: 'cleaning',
        reservedDate: '2026-05-08T14:00:00',
        partnerName: '이음홈케어',
        partnerManagerName: '김민수',
        partnerManagerPhone: '010-2480-0995',
        payload: { responseKind: 'schedule_change' },
      });
      setLoading(false);
      if (previewStep === 'change') {
        setDate('2026-05-13');
        setTime('18:03');
      }
      return () => { alive = false; };
    }
    resolveShortLink(code)
      .then((d) => { if (alive) { setLink(d); setLoading(false); } })
      .catch((e) => { if (alive) { setLoadError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [code, previewDone, previewStep]);

  const kind = link?.payload?.responseKind || link?.responseKind || 'schedule_change';
  const meta = KIND_META[kind] || KIND_META.schedule_change;
  const isPartnerChange = kind === 'partner_change';

  const categoryLabel = CATEGORY_LABEL[link?.category] || link?.category || '';
  const scheduledRaw = link?.reservedDate || link?.visitDate || link?.movingDate || '';
  const scheduledDate = formatDateKR(scheduledRaw);
  const scheduledTime = formatTimeKR(scheduledRaw);

  const handleConfirm = async () => {
    if (!window.confirm('일정을 확인하셨나요?')) return;
    setSubmitting(true);
    try {
      if (!previewDone && !previewStep) {
        await submitShortLinkAction({ code, action: 'schedule_confirmed' });
      }
      setStep('done');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('상담을 취소하시겠어요?')) return;
    setSubmitting(true);
    try {
      if (!previewDone && !previewStep) {
        await submitShortLinkAction({ code, action: 'cancel_final' });
      }
      setStep('done');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeSubmit = async () => {
    setFormError('');
    if (meta.needsDate && !date) { setFormError('희망 날짜를 선택해주세요.'); return; }
    setSubmitting(true);
    try {
      if (!previewDone && !previewStep) {
        await submitShortLinkResponse({
          code,
          responseKind: kind,
          newDate: date,
          newTime: time,
          memo: memo.trim(),
        });
      }
      setStep('done');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // 7-5 [다른 업체 연결] 전용 — 요청사항 입력 → 고객 요청사항(customers.memo) 에 저장
  const handlePartnerChangeSubmit = async () => {
    setFormError('');
    if (!memo.trim()) { setFormError('요청사항을 입력해 주세요.'); return; }
    setSubmitting(true);
    try {
      if (!previewDone && !previewStep) {
        await submitShortLinkResponse({
          code,
          responseKind: 'partner_change',
          memo: memo.trim(),
        });
      }
      setStep('done');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page><Centered><Muted>불러오는 중...</Muted></Centered></Page>;
  }

  if (loadError) {
    return (
      <Page>
        <PageWrap>
          <Header />
          <Card>
            <Title>안내</Title>
            <Muted>{loadError}</Muted>
            <FooterNote>
              문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
            </FooterNote>
          </Card>
        </PageWrap>
      </Page>
    );
  }

  if (step === 'done' || link?.used) {
    const alreadyUsed = step !== 'done' && link?.used;
    return (
      <Page>
        <PageWrap>
          <Header />
          <DoneCard>
            <DoneIllust src="/illust-submit-complete.png" alt="" />
            {alreadyUsed ? (
              <>
                <DoneSub>이미 응답이 접수된</DoneSub>
                <DoneTitle>안내 페이지입니다</DoneTitle>
                <DoneDesc>이전에 보내주신 내용으로 담당자가 확인 중입니다.<br />추가 문의는 아래 번호로 연락주세요.</DoneDesc>
              </>
            ) : (
              <>
                <DoneSub>응답이 정상적으로</DoneSub>
                <DoneTitle>접수되었습니다</DoneTitle>
                <DoneDesc>담당자가 확인 후 등록하신 연락처로 안내드릴 예정입니다.<br />이 창은 닫으셔도 됩니다.</DoneDesc>
              </>
            )}
          </DoneCard>
          <FooterNote>
            문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
          </FooterNote>
        </PageWrap>
      </Page>
    );
  }

  return (
    <Page>
      <PageWrap>
        <Header />

        {(link?.customerName || link?.customerAddress) && (
          <InfoCard>
            <InfoRow>
              <InfoLabel>고객명</InfoLabel>
              <InfoValue>{link?.customerName || '-'}</InfoValue>
            </InfoRow>
            {link?.customerAddress && (
              <InfoRow>
                <InfoLabel>주소지</InfoLabel>
                <InfoValue>{link.customerAddress}</InfoValue>
              </InfoRow>
            )}
          </InfoCard>
        )}

        {(categoryLabel || scheduledDate) && (
          <InfoCard>
            {categoryLabel && (
              <InfoRow $right>
                <InfoLabel>신청 서비스</InfoLabel>
                <InfoStrong>{categoryLabel}</InfoStrong>
              </InfoRow>
            )}
            {scheduledDate && (
              <InfoRow $right>
                <InfoLabel>{kind === 'schedule_change' ? '이사 예정일' : '예정일'}</InfoLabel>
                <InfoStrong>{scheduledDate}</InfoStrong>
              </InfoRow>
            )}
          </InfoCard>
        )}

        {(link?.partnerName || link?.partnerManagerPhone) && (
          <SplitRow>
            {link?.partnerName && (
              <SplitCard>
                <SplitHead><IoBusinessOutline /> 담당 업체</SplitHead>
                <SplitBody>{link.partnerName}</SplitBody>
              </SplitCard>
            )}
            {link?.partnerManagerPhone && (
              <SplitCard>
                <SplitHead><IoCallOutline /> 담당자 / 연락처</SplitHead>
                <SplitBody>
                  {link.partnerManagerName && <div>{link.partnerManagerName}</div>}
                  <PhoneLink href={`tel:${link.partnerManagerPhone.replace(/-/g, '')}`}>
                    {link.partnerManagerPhone}
                  </PhoneLink>
                </SplitBody>
              </SplitCard>
            )}
          </SplitRow>
        )}

        {!isPartnerChange && step === 'confirm' && scheduledDate && (
          <ScheduleCard>
            <ScheduleHead><IoCalendarOutline /> 예약 일정</ScheduleHead>
            <ScheduleRow>
              <VerifyLine>
                <ScheduleDateText>
                  {scheduledDate}{scheduledTime ? ` ${scheduledTime}` : ''}
                </ScheduleDateText>
              </VerifyLine>
              <SmallBtn type="button" onClick={() => setStep('change')}>일정변경</SmallBtn>
            </ScheduleRow>

            <ConfirmAsk>
              <IoHelpCircle />
              <span><strong>예약된 일정을 확인해주세요.</strong></span>
            </ConfirmAsk>
            <ConfirmDesc>
              일정이 맞으면, 일정 확정을 위해 확인을 눌러주세요.<br />
              일정을 변경한 경우에는 변경일정으로 재접수됩니다.
            </ConfirmDesc>
          </ScheduleCard>
        )}

        {!isPartnerChange && step === 'change' && (
          <ScheduleDialPicker
            baseRaw={scheduledRaw}
            date={date}
            time={time}
            onChange={({ date: d, time: t }) => { setDate(d); setTime(t); }}
            onApply={handleChangeSubmit}
            onCancel={() => { setStep('confirm'); setDate(''); setTime(''); }}
            applying={submitting}
          />
        )}

        {isPartnerChange && step !== 'done' && (
          <Card>
            <ChangeFormBlock>
              <InlineField>
                <InlineLabel>{meta.memoLabel || '요청 내용'}</InlineLabel>
                <Textarea
                  rows={5}
                  value={memo}
                  placeholder={meta.desc || '요청 사항을 입력해 주세요.'}
                  onChange={(e) => setMemo(e.target.value)}
                />
              </InlineField>
            </ChangeFormBlock>
          </Card>
        )}

        {formError && <ErrorText>{formError}</ErrorText>}

        {/* 하단 버튼 영역 */}
        {!isPartnerChange && step === 'confirm' && (
          <BtnRow>
            <PrimaryBtn type="button" onClick={handleConfirm} disabled={submitting}>
              일정확인
            </PrimaryBtn>
            <SecondaryBtn type="button" onClick={handleCancel} disabled={submitting}>
              취소하기
            </SecondaryBtn>
          </BtnRow>
        )}

        {isPartnerChange && step !== 'done' && (
          <PrimaryBtn type="button" onClick={handlePartnerChangeSubmit} disabled={submitting || !memo.trim()}>
            {submitting ? '제출 중...' : '요청 보내기'}
          </PrimaryBtn>
        )}

        <FooterNote>
          문의 <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}>{CONTACT_PHONE}</a>
        </FooterNote>
      </PageWrap>
    </Page>
  );
}

function Header() {
  return (
    <HeaderWrap>
      <LogoText>이음 <small>IEUM</small></LogoText>
    </HeaderWrap>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: #EEF2FF;
  display: flex;
  justify-content: center;
  padding: 16px 14px 40px;
`;

const PageWrap = styled.div`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const HeaderWrap = styled.div`
  padding: 6px 4px 8px;
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #4A6CF7;
  letter-spacing: -0.5px;
  small {
    font-size: 9px;
    font-weight: 600;
    color: #4A6CF7;
    margin-left: 2px;
    letter-spacing: 1px;
  }
`;

const InfoCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  ${({ $right }) => $right && `justify-content: space-between;`}
`;

const InfoLabel = styled.div`
  font-size: 13px;
  color: #6B7280;
  min-width: 64px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: #1F2937;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`;

const InfoStrong = styled.div`
  font-size: 15px;
  color: #1F2937;
  font-weight: 700;
`;

const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const SplitCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SplitHead = styled.div`
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  svg { color: #4A6CF7; font-size: 16px; }
`;

const SplitBody = styled.div`
  font-size: 14px;
  color: #1F2937;
  font-weight: 600;
  line-height: 1.5;
`;

const PhoneLink = styled.a`
  font-size: 14px;
  color: #1F2937;
  font-weight: 600;
  text-decoration: none;
`;

const ScheduleCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ScheduleHead = styled.div`
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  svg { color: #4A6CF7; font-size: 16px; }
`;

const ScheduleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
`;

const ScheduleDateText = styled.div`
  font-size: 18px;
  color: #1F2937;
  font-weight: 800;
`;

const SmallBtn = styled.button`
  border: 1px solid #4A6CF7;
  background: #fff;
  color: #4A6CF7;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
`;

const ConfirmAsk = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 14px;
  color: #1F2937;
  svg { color: #4A6CF7; font-size: 18px; }
`;

const ConfirmDesc = styled.div`
  font-size: 12px;
  color: #6B7280;
  text-align: center;
  line-height: 1.6;
`;

const VerifyLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChangeFormBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px dashed #E5E7EB;
`;

const InlineField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InlineLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
`;

const Card = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 16px;
  padding: 28px 22px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 8px;
  text-align: center;
`;

const Textarea = styled.textarea`
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  color: #1F2937;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;

  &::placeholder { color: #9CA3AF; }
  &:focus {
    outline: none;
    border-color: #4A6CF7;
  }
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #DC2626;
  margin: 4px 0 0;
  text-align: center;
`;

const BtnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 4px;
`;

const PrimaryBtn = styled.button`
  min-height: 50px;
  border: 0;
  border-radius: 12px;
  background: #4A6CF7;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: #3F5BD9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const SecondaryBtn = styled.button`
  min-height: 50px;
  border: 0;
  border-radius: 12px;
  background: #E5E7EB;
  color: #4B5563;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) { background: #D1D5DB; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const DoneCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 30px 24px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  text-align: center;
`;

const DoneIllust = styled.img`
  width: 180px;
  height: 180px;
  margin: 0 auto 16px;
  display: block;
  object-fit: contain;
`;

const DoneSub = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 4px;
`;

const DoneTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #1F2937;
  margin-bottom: 10px;
`;

const DoneDesc = styled.div`
  font-size: 12px;
  color: #6B7280;
  line-height: 1.6;
`;

const Muted = styled.p`
  font-size: 14px;
  color: #6B7280;
  line-height: 1.6;
  text-align: center;
  margin: 0;
`;

const FooterNote = styled.p`
  margin: 14px 0 0;
  font-size: 12px;
  color: #9CA3AF;
  text-align: center;

  a {
    color: #4A6CF7;
    font-weight: 700;
    text-decoration: none;
  }
`;
